import { memo, useCallback, useMemo, useState } from 'react';
import { Button, Text, TextInput } from '@mantine/core';
import { IconPlus, IconSearch } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { AppScrollArea } from '@/components/ui/AppScrollArea';
import { useWorkspace } from '@/hooks/useWorkspace';
import { useGeoMarkupStore } from '@/store';
import { WorkspaceMenuItem } from './WorkspaceMenuItem';
import type { WorkspaceSelectorContentProps } from './WorkspaceSelector.types';
import type { WorkspaceListItem } from '@/types/workspace';
import styles from './WorkspaceSelector.module.css';

const WORKSPACE_LIST_MAX_HEIGHT = 240;
const MENU_ICON_SIZE = 14;

export const WorkspaceSelectorContent = memo(function WorkspaceSelectorContent({
  onWorkspaceSwitch,
  onAfterAction,
}: WorkspaceSelectorContentProps) {
  const { t } = useTranslation();

  const { workspaces, currentWorkspaceId } = useWorkspace();

  const openCreateModal = useGeoMarkupStore(
    (state) => state.workspace.openCreateModal
  );

  const [searchQuery, setSearchQuery] = useState('');

  const filteredWorkspaces = useMemo(() => {
    if (!searchQuery.trim()) return workspaces;
    const query = searchQuery.toLowerCase();
    return workspaces.filter((workspace) =>
      workspace.name.toLowerCase().includes(query)
    );
  }, [workspaces, searchQuery]);

  const handleSelect = useCallback(
    (workspace: WorkspaceListItem) => {
      onWorkspaceSwitch(workspace.workspaceId, workspace.shortId);
      onAfterAction?.();
    },
    [onWorkspaceSwitch, onAfterAction]
  );

  const handleCreateClick = useCallback(() => {
    openCreateModal();
    onAfterAction?.();
  }, [openCreateModal, onAfterAction]);

  return (
    <>
      <div className={styles.searchSection}>
        <TextInput
          placeholder={t('workspace.dashboard.searchPlaceholder')}
          leftSection={<IconSearch size={MENU_ICON_SIZE} />}
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.currentTarget.value)}
          size="sm"
          autoFocus
        />
      </div>

      <AppScrollArea maxHeight={WORKSPACE_LIST_MAX_HEIGHT}>
        <div className={styles.listSection}>
          {filteredWorkspaces.map((workspace) => (
            <WorkspaceMenuItem
              key={workspace.workspaceId}
              workspace={workspace}
              isActive={workspace.workspaceId === currentWorkspaceId}
              onSelect={handleSelect}
            />
          ))}
          {filteredWorkspaces.length === 0 && (
            <div className={styles.emptyState}>
              <Text size="sm" c="dimmed">
                {t('workspace.dashboard.noResults')}
              </Text>
            </div>
          )}
        </div>
      </AppScrollArea>

      <div className={styles.footerSection}>
        <Button
          variant="subtle"
          leftSection={<IconPlus size={MENU_ICON_SIZE} />}
          onClick={handleCreateClick}
          fullWidth
          justify="center"
        >
          {t('workspace.dashboard.create')}
        </Button>
      </div>
    </>
  );
});
